/**
 * Token utility functions for JWT management
 */

/**
 * Check if a token is expired
 * @param {string} token - Auth token (UUID format from your API)
 * @returns {boolean} - True if token is expired
 */
export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    // For UUID tokens from your API, check timestamp
    const tokenTimestamp = localStorage.getItem("tokenTimestamp");
    const expiresAt = localStorage.getItem("tokenExpiresAt");

    if (expiresAt) {
      // Check against API-provided expiry time
      const expiryTime = new Date(expiresAt);
      const currentTime = new Date();
      const fiveMinutesBuffer = 5 * 60 * 1000; // 5 minutes in ms

      return currentTime.getTime() > expiryTime.getTime() - fiveMinutesBuffer;
    } else if (tokenTimestamp) {
      // Fallback to 24-hour expiry if no specific expiry time
      const storedTime = parseInt(tokenTimestamp);
      const currentTime = Date.now();
      const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in ms

      return currentTime > storedTime + twentyFourHours;
    }

    // If no timestamp info, consider expired
    return true;
  } catch (error) {
    console.error("Error checking token expiry:", error);
    return true;
  }
};

/**
 * Get token expiration time
 * @param {string} token - JWT token
 * @returns {Date|null} - Expiration date or null if invalid
 */
export const getTokenExpiration = (token) => {
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return new Date(payload.exp * 1000);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

/**
 * Get user info from token
 * @param {string} token - JWT token
 * @returns {object|null} - User info or null if invalid
 */
export const getUserFromToken = (token) => {
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return {
      id: payload.sub || payload.userId,
      role: payload.role,
      email: payload.email,
      full_name: payload.full_name,
      exp: payload.exp,
    };
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

/**
 * Store auth token and user data
 * @param {string} token - Auth token from API
 * @param {object} user - User object
 * @param {string} expiresAt - Optional expiry time from API
 */
export const storeAuthData = (token, user, expiresAt = null) => {
  try {
    localStorage.setItem("authToken", token);
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("tokenTimestamp", Date.now().toString());

    // Store API-provided expiry time if available
    if (expiresAt) {
      localStorage.setItem("tokenExpiresAt", expiresAt);
    }
  } catch (error) {
    console.error("Error storing auth data:", error);
  }
};

/**
 * Get stored auth token
 * @returns {string|null} - Auth token or null if not found/expired
 */
export const getStoredToken = () => {
  try {
    const token = localStorage.getItem("authToken");

    if (!token) return null;

    // Check if token is expired
    if (isTokenExpired(token)) {
      clearAuthData();
      return null;
    }

    return token;
  } catch (error) {
    console.error("Error retrieving token:", error);
    clearAuthData();
    return null;
  }
};

/**
 * Get stored user data
 * @returns {object|null} - User object or null if not found
 */
export const getStoredUser = () => {
  try {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error("Error retrieving user data:", error);
    return null;
  }
};

/**
 * Clear all auth data from localStorage
 */
export const clearAuthData = () => {
  try {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("tokenTimestamp");
    localStorage.removeItem("tokenExpiresAt");
  } catch (error) {
    console.error("Error clearing auth data:", error);
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean} - True if user has valid token
 */
export const isAuthenticated = () => {
  const token = getStoredToken();
  return !!token && !isTokenExpired(token);
};

/**
 * Store selected client_id in localStorage
 * @param {string} clientId - Client ID to store
 */
export const storeClientId = (clientId) => {
  try {
    if (clientId) {
      localStorage.setItem("selectedClientId", clientId);
    } else {
      localStorage.removeItem("selectedClientId");
    }
  } catch (error) {
    console.error("Error storing client_id:", error);
  }
};

/**
 * Get stored client_id from localStorage
 * @returns {string|null} - Stored client ID or null
 */
export const getStoredClientId = () => {
  try {
    return localStorage.getItem("selectedClientId");
  } catch (error) {
    console.error("Error retrieving client_id:", error);
    return null;
  }
};

/**
 * Clear stored client_id from localStorage
 */
export const clearStoredClientId = () => {
  try {
    localStorage.removeItem("selectedClientId");
  } catch (error) {
    console.error("Error clearing client_id:", error);
  }
};
